"use client";

import React from "react";
import { MapPin, Phone, Mail, Facebook, Twitter, Instagram, Linkedin, Loader, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
const contactDetails = [{
  icon: MapPin,
  text: "Calgary, Alberta | Commercial & Residential"
}, {
  icon: Phone,
  text: "+1 (403) 966-9321"
}, {
  icon: Mail,
  text: "lorenzo@LCREinc.ca"
}];
const socialLinks = [{
  icon: Facebook,
  href: "#",
  label: "Facebook"
}, {
  icon: Twitter,
  href: "#",
  label: "Twitter"
}, {
  icon: Instagram,
  href: "#",
  label: "Instagram"
}, {
  icon: Linkedin,
  href: "#",
  label: "LinkedIn"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-secondary/10 text-secondary rounded-md flex items-center justify-center mb-6">
          <Mail className="size-8" />
        </div>
        <h3 className="font-default text-2xl font-bold text-foreground mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  const inputStyles = "border-x-0 border-t-0 border-b border-border rounded-none shadow-none px-0 bg-transparent focus-visible:ring-0 focus-visible:border-secondary text-foreground placeholder:text-muted-foreground/30 md:text-base h-12 transition-colors duration-300";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-semibold font-default">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Lorenzo" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-semibold font-default">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Cabason" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-semibold font-default">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="lorenzo@example.com" className={inputStyles} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-semibold font-default">Your Inquiry</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your investment goals or property needs..." className={`resize-none min-h-[120px] ${inputStyles}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-full bg-primary text-secondary hover:bg-primary/90 rounded-md px-8 py-6 text-base font-semibold uppercase tracking-widest group transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : <>
              Inquire Now
              <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function StartYourNextVenture() {
  return <section data-section-id="252252" id="start-your-next-venture" className="bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          
          {}
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <div className="mb-12 relative aspect-[4/5] overflow-hidden rounded-md shadow-xl grayscale">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687293/img/lorenzo_cabason_portrait.png" alt="Lorenzo Cabason" className="object-cover w-full !h-[480px] ![padding:0px_0px_20px_0px]" src="https://distinct-macaw.10web.cloud/wp-content/uploads/2026/05/Proffesional_Lorenzo_Picture_closeup-scaled.jpeg" />
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
                Start Your Next Venture.
              </h2>
              <p className="text-muted-foreground text-lg mb-12 max-w-md leading-relaxed font-default">
                Providing unparalleled value to infill builders, home buyers, and investors. Let's discuss your real estate strategy.
              </p>

              <div className="space-y-8">
                {contactDetails.map((detail, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                    <div className="mt-1 p-3 bg-muted rounded-md text-foreground group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                      <detail.icon className="size-5" />
                    </div>
                    <div>
                      <p className="text-foreground font-medium text-lg font-default">{detail.text}</p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="mt-16 pt-12 border-t border-border">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-6 font-default">
                Connect with the firm
              </h4>
              <div className="flex gap-4">
                {socialLinks.map((social, i) => <Link key={i} data-index={i} to={social.href} className="p-3 bg-muted rounded-md text-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors duration-300" aria-label={social.label}>
                    <social.icon className="size-5" />
                  </Link>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="bg-card/30 backdrop-blur-md border border-border/40 p-8 md:p-12 rounded-md shadow-md">
              <FormProvider formKey="venture_inquiry_form_home" formId="venture_inquiry_form_home" sectionName="Contact & Inquiry Form" defaultValues={{
              firstName: "",
              lastName: "",
              email: "",
              message: ""
            }} submitText="Inquire Now" successMessage="Thank you for reaching out. Lorenzo or a member of the team will contact you shortly to discuss your requirements.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}